import React, { useState } from 'react';
import styled from 'styled-components';
import { useCart } from 'contexts/CartContext';
import { createCart, getVariantBySize } from 'lib/shopify';

function MobileCart({ onBack, onShowDialog, onViewProduct }) {
  const { items, removeItem, updateQuantity, clearCart, getTotal, getItemCount } = useCart();
  const [checkingOut, setCheckingOut] = useState(false);

  async function handleCheckout() {
    setCheckingOut(true);
    try {
      const lines = items.map(item => {
        const variantId = item.product.variants
          ? getVariantBySize(item.product, item.size, item.color)
          : null;
        return { variantId, quantity: item.quantity, name: item.product.name };
      });
      const invalid = lines.find(l => !l.variantId);
      if (invalid) {
        onShowDialog && onShowDialog('error', `"${invalid.name}" variant is unavailable. Please update your cart.`);
        setCheckingOut(false);
        return;
      }
      const cart = await createCart(lines.map(l => ({ variantId: l.variantId, quantity: l.quantity })));
      window.location.href = cart.checkoutUrl;
    } catch (err) {
      onShowDialog && onShowDialog('error', `Checkout failed: ${err.message}`);
    }
    setCheckingOut(false);
  }

  return (
    <Div>
      <div className="mc-top">
        <button className="mc-back" onClick={onBack}>← Back</button>
        <span className="mc-title">Cart ({getItemCount()})</span>
      </div>

      <div className="mc-scroll">
        {items.length === 0 ? (
          <div className="mc-empty">Your cart is empty</div>
        ) : (
          items.map(item => (
            <div key={`${item.product.id}-${item.size}-${item.color}`} className="mc-item">
              <div className="mc-item__img" onClick={() => onViewProduct && onViewProduct(item.product, { size: item.size, color: item.color })}>
                <img src={
                  (item.color && item.product.colorImages && item.product.colorImages[item.color]?.[0])
                  || item.product.images[0]
                } alt={item.product.name} />
              </div>
              <div className="mc-item__info">
                <div className="mc-item__name" onClick={() => onViewProduct && onViewProduct(item.product, { size: item.size, color: item.color })}>{item.product.name}</div>
                <div className="mc-item__meta">
                  {item.color && <span>{item.color} / </span>}Size: {item.size}
                </div>
                <div className="mc-item__price">₹{item.product.price.toLocaleString()}</div>
                <div className="mc-item__qty">
                  <button onClick={() => updateQuantity(item.product.id, item.size, item.color, item.quantity - 1)}>−</button>
                  <span>{item.quantity}</span>
                  <button onClick={() => updateQuantity(item.product.id, item.size, item.color, item.quantity + 1)}>+</button>
                </div>
              </div>
              <div className="mc-item__right">
                <div className="mc-item__subtotal">₹{(item.product.price * item.quantity).toLocaleString()}</div>
                <button className="mc-item__remove" onClick={() => removeItem(item.product.id, item.size, item.color)}>✕</button>
              </div>
            </div>
          ))
        )}
      </div>

      {items.length > 0 && (
        <div className="mc-footer">
          <div className="mc-summary">
            <div className="mc-row">
              <span>Subtotal</span>
              <span>₹{getTotal().toLocaleString()}</span>
            </div>
            <div className="mc-row">
              <span>Shipping</span>
              <span>{getTotal() >= 999 ? 'FREE' : '₹99'}</span>
            </div>
            <div className="mc-row mc-row--total">
              <span>Total</span>
              <span>₹{(getTotal() + (getTotal() >= 999 ? 0 : 99)).toLocaleString()}</span>
            </div>
          </div>
          <div className="mc-actions">
            <button className="mc-clear" onClick={() => clearCart()}>Clear</button>
            <button className="mc-checkout" onClick={handleCheckout} disabled={checkingOut}>
              {checkingOut ? 'Loading...' : 'Safe Checkout'}
            </button>
          </div>
        </div>
      )}
    </Div>
  );
}

const Div = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #1a1a1a;
  color: #c0c0c0;
  font-family: Tahoma, sans-serif;

  .mc-top {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    background: #222;
    border-bottom: 2px solid #f5a623;
  }
  .mc-back {
    background: none;
    border: none;
    color: #f5a623;
    font-size: 14px;
    font-family: Tahoma, sans-serif;
    cursor: pointer;
  }
  .mc-title {
    font-size: 16px;
    font-weight: bold;
    color: #e0e0e0;
  }

  .mc-scroll {
    flex: 1;
    overflow-y: auto;
  }
  .mc-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #555;
    font-size: 16px;
  }

  .mc-item {
    display: flex;
    gap: 12px;
    padding: 12px 14px;
    border-bottom: 1px solid #2a2a2a;
  }
  .mc-item__img {
    width: 70px;
    height: 90px;
    flex-shrink: 0;
    background: #111;
    border: 1px solid #333;
    overflow: hidden;
    img { width: 100%; height: 100%; object-fit: cover; }
  }
  .mc-item__info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .mc-item__name {
    font-size: 13px;
    font-weight: bold;
    color: #ddd;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .mc-item__meta { font-size: 11px; color: #888; }
  .mc-item__price { font-size: 12px; color: #999; }
  .mc-item__qty {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 4px;
    button {
      width: 28px;
      height: 28px;
      background: #222;
      border: 1px solid #444;
      color: #ccc;
      font-size: 16px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    span { font-size: 14px; font-weight: bold; color: #e0e0e0; }
  }
  .mc-item__right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: space-between;
    flex-shrink: 0;
  }
  .mc-item__subtotal {
    font-size: 14px;
    font-weight: bold;
    color: #f5a623;
  }
  .mc-item__remove {
    background: none;
    border: none;
    color: #666;
    font-size: 16px;
    cursor: pointer;
    &:hover { color: #ff4444; }
  }

  .mc-footer {
    padding: 12px 14px;
    background: #1e1e1e;
    border-top: 1px solid #333;
  }
  .mc-summary {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 10px;
  }
  .mc-row {
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    color: #999;
  }
  .mc-row--total {
    font-size: 16px;
    font-weight: bold;
    color: #e0e0e0;
    padding-top: 6px;
    border-top: 1px solid #333;
    margin-top: 4px;
  }
  .mc-actions {
    display: flex;
    gap: 8px;
  }
  .mc-clear {
    background: #222;
    border: 1px solid #444;
    color: #999;
    padding: 12px 16px;
    font-size: 13px;
    font-family: Tahoma, sans-serif;
    cursor: pointer;
  }
  .mc-checkout {
    flex: 1;
    background: linear-gradient(180deg, #f5a623 0%, #d4891a 100%);
    border: none;
    color: #000;
    padding: 12px;
    font-size: 15px;
    font-weight: bold;
    font-family: Tahoma, sans-serif;
    cursor: pointer;
    &:disabled { opacity: 0.6; }
  }
`;

export default MobileCart;
